import React, { useState } from "react";
import { Button, Form, Modal, ModalBody, ModalFooter, ModalHeader, InputGroup } from "react-bootstrap";
import { Tracker } from "../../state/Tracker";
import { LifePart } from "../../state/LifePart";
import { useData } from "../../storage/accessors";
import { useReadonly } from "@/contexts/readonlyContext";

interface Props {
    show: boolean;
    onHide: () => void;
    onSave: (tracker: Tracker) => void;
    onDelete?: (uuid: string) => void;
    tracker?: Tracker | null;
    lifeParts: LifePart[];
    defaultLifePart?: string;
}

export default function TrackerModal({ show, onHide, onSave, onDelete, tracker, lifeParts, defaultLifePart }: Props) {
    const { trackers } = useData();
    const { readonly } = useReadonly();
    const [name, setName] = useState(tracker?.name || "");
    const [lifepart, setLifepart] = useState(tracker?.lifepart || defaultLifePart || "");
    const [measure, setMeasure] = useState(tracker?.measure || "");
    const [target, setTarget] = useState(tracker?.target !== undefined ? String(tracker.target) : "");
    const [targetIndicator, setTargetIndicator] = useState<Tracker["targetIndicator"]>(tracker?.targetIndicator || "MORE");
    const [error, setError] = useState<string>("");

    const handleSave = () => {
        if (readonly) return;
        setError("");

        if (!name || !lifepart) {
            setError("Заполните название и выберете сферу жизни из баланса");
            return;
        }

        const order = !tracker
            ? Object.values(trackers.objects).filter(t => t.lifepart === lifepart).length
            : tracker.order || 0;

        const trackerData: Tracker = {
            uuid: tracker?.uuid || crypto.randomUUID(),
            name,
            lifepart,
            measure,
            display: "SIMPLE",
            group: "NO",
            order,
            target: target ? parseFloat(target) : undefined,
            targetIndicator: target ? targetIndicator : undefined
        };

        onSave(trackerData);
        if (!tracker) {
            setName("");
            setLifepart(defaultLifePart || "");
            setMeasure("");
            setTarget("");
            setTargetIndicator("MORE");
            setError("");
        }
        onHide();
    };


    const handleDelete = () => {
        if (tracker && onDelete) {
            const confirmed = confirm(
                `Вы уверены, что хотите удалить трекер "${tracker.name}"?\n\n` +
                `Все сохранённые значения этого трекера будут безвозвратно удалены.`
            );
            if (confirmed) {
                onDelete(tracker.uuid);
                onHide();
            }
        }
    };

    return (
        <Modal show={show} onHide={onHide} dialogClassName="shadow-lg">
            <ModalHeader closeButton>
                {tracker ? "Редактировать трекер" : "Новый трекер"}
            </ModalHeader>
            <ModalBody>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <Form.Group className="mb-3">
                    <Form.Label>Название</Form.Label>
                    <Form.Control
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Например: Спорт, Сон, Питание"
                        autoFocus
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Сфера баланса</Form.Label>
                    <Form.Select value={lifepart} onChange={(e) => setLifepart(e.target.value)}>
                        <option value="">Выберите сферу баланса</option>
                        {lifeParts.sort((a, b) => (a.order || 0) - (b.order || 0)).map((lp) => (
                            <option key={lp.uuid} value={lp.uuid}>{lp.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Единица измерения</Form.Label>
                    <Form.Control
                        value={measure}
                        onChange={(e) => setMeasure(e.target.value)}
                        placeholder="Например: мин, км, кг, часы"
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Цель (необязательно)</Form.Label>
                    <InputGroup>
                        <Form.Select
                            value={targetIndicator}
                            onChange={(e) => setTargetIndicator(e.target.value as Tracker["targetIndicator"])}
                            disabled={!target}
                            style={{ maxWidth: '140px' }}
                        >
                            <option value="MORE">Больше</option>
                            <option value="LESS">Меньше</option>
                        </Form.Select>
                        <Form.Control
                            type="number"
                            step="any"
                            value={target}
                            onChange={(e) => setTarget(e.target.value)}
                            placeholder="100"
                        />
                    </InputGroup>
                </Form.Group>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
                {tracker && onDelete ? (
                    <Button variant="outline-danger" onClick={handleDelete}>
                        Удалить
                    </Button>
                ) : (
                    <div />
                )}
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={onHide}>Отмена</Button>
                    <Button
                        variant="outline-success"
                        onClick={handleSave}
                        disabled={!name || !lifepart}
                    >
                        Сохранить
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
}
